import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Column } from 'src/shared/components/Semantic/all'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { adminOrdersPath } from 'src/routes/paths'
import { effectiveQuoteStatus } from 'src/shared/consts/quoteStatus'
import {
  RequestProvider,
  useRequestContext,
} from 'src/pages/_myAccount/Requests/Request/hooks/RequestContext'
import { useOrderViewState } from 'src/pages/_myAccount/Requests/Request/hooks/useOrderViewState'
import { RequestFloatingNav } from 'src/pages/_myAccount/Requests/Request/components/RequestFloatingNav'
import { RequestChatSidebar } from 'src/pages/_myAccount/Requests/Request/components/RequestChatSidebar'
import { RequestBottomNav } from 'src/pages/_myAccount/Requests/Request/components/RequestBottomNav'
import { OrderCatalogWidget } from 'src/pages/_myAccount/Requests/Request/components/OrderCatalogWidget'
import { OrderCatalogControlProvider } from 'src/pages/_myAccount/Requests/Request/components/OrderCatalogWidget/OrderCatalogControlContext'
import { RequestPageContent } from 'src/pages/_myAccount/Requests/Request/components/RequestPageContent'
import type { RequestTabId } from 'src/pages/_myAccount/Requests/Request/types'

const defaultTabForStatus = (
  status: string | null | undefined
): RequestTabId => {
  const s = effectiveQuoteStatus(status)
  if (
    s === '1_5_sales_approved_quote' ||
    s === '2_approved_quote' ||
    s === '3_approved_proofs'
  ) {
    return 'checkout'
  }
  if (
    s === '4_started_production' ||
    s === '5_awaiting_shipment' ||
    s === '8_delivered'
  ) {
    return 'delivery'
  }
  return 'explore'
}

const AdminOrderWorkspaceContent = () => {
  const { quote, quoteItems, isQuotePending } = useRequestContext()
  const {
    activeTab: urlTab,
    setTab,
    setView,
    hasViewInUrl,
  } = useOrderViewState(adminOrdersPath)
  const initializedQuoteId = useRef<number | null>(null)
  const [catalogOpen, setCatalogOpen] = useState(false)

  useEffect(() => {
    if (!quote?.id) return
    if (hasViewInUrl) {
      initializedQuoteId.current = quote.id
      return
    }
    if (initializedQuoteId.current === quote.id) return
    initializedQuoteId.current = quote.id
    setView(defaultTabForStatus(quote.status))
    // Intentionally omit setView — sync once per quote when URL has no view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id, quote?.status, hasViewInUrl])

  const activeTab = urlTab ?? defaultTabForStatus(quote?.status)
  const hasItems = (quoteItems?.length ?? 0) > 0

  if (isQuotePending && !quote) {
    return <FullScreenLoader />
  }

  return (
    <OrderCatalogControlProvider
      value={{
        openCatalog: () => setCatalogOpen(true),
        hasItems,
      }}
    >
      <Column className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <RequestFloatingNav
          backHref={
            quote?.org_id
              ? adminOrdersPath
              : `${adminOrdersPath}?unassigned=1`
          }
          editableStatus
          onStatusUpdated={(next) => {
            setView(defaultTabForStatus(next))
          }}
        />

        <Column className="relative min-h-0 flex-1 overflow-hidden">
          <RequestPageContent activeTab={activeTab} />
          <RequestChatSidebar activeTab={activeTab} />
          <RequestBottomNav activeTab={activeTab} onTabChange={setTab} />
        </Column>

        {quote?.id != null && (
          <OrderCatalogWidget
            quoteId={quote.id}
            open={catalogOpen}
            onOpenChange={setCatalogOpen}
          />
        )}
      </Column>
    </OrderCatalogControlProvider>
  )
}

export const AdminOrderPage = () => {
  const { public_id } = useParams<{ public_id: string }>()

  return (
    <Column className="flex h-full min-h-0 flex-col overflow-hidden">
      <RequestProvider publicId={public_id}>
        <AdminOrderWorkspaceContent />
      </RequestProvider>
    </Column>
  )
}
